import React from "react";

const ContentList = ({ subject, content, onAddContent, onEditContent, onDeleteContent }) => {
  if (!content) {
    return (
      <div className="content-section px-20 pb-15 bg-light-5">
        <div className="text-center py-20">
          <div className="text-13">Loading content...</div>
        </div>
      </div>
    );
  }

  const hasContent = content.videos?.length > 0 || content.pdfs?.length > 0 || content.mockTests?.length > 0;

  if (!hasContent) {
    return (
      <div className="content-section px-20 pb-15 bg-light-5">
        <div className="text-center py-20">
          <div className="text-13 text-light-1 mb-10">No content found for this subject</div>
          <div className="d-flex justify-center x-gap-10">
            <button 
              onClick={() => onAddContent('video')}
              className="content-action-btn bg-black text-white"
            >
              <i className="icon-video"></i>
              <span>Add Video</span>
            </button>
            <button 
              onClick={() => onAddContent('pdf')}
              className="content-action-btn bg-black text-white"
            >
              <i className="icon-file"></i>
              <span>Add Material</span>
            </button>
            <button 
              onClick={() => onAddContent('test')}
              className="content-action-btn bg-black text-white"
            >
              <i className="icon-test"></i>
              <span>Add Test</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-section px-20 pb-15 bg-light-5">
      <div className="y-gap-25">
        {/* Videos Section */}
        {content.videos?.length > 0 && (
          <div className="content-group">
            <div className="d-flex items-center justify-between mb-15">
              <div className="d-flex items-center">
                <i className="icon-video text-green-1 mr-8"></i>
                <h5 className="text-16 fw-600">Videos ({content.videos.length})</h5>
              </div>
              <button 
                onClick={() => onAddContent('video')}
                className="button -sm bg-green-1 text-white fw-500"
              >
                <i className="icon-plus mr-5"></i>
                Add Video
              </button>
            </div>
            <div className="row y-gap-10">
              {content.videos.map((video) => (
                <div key={video._id} className="col-12">
                  <div className="content-item bg-white rounded-6 px-15 py-12 d-flex items-center justify-between">
                    <div className="d-flex items-center">
                      <i className="icon-play text-green-1 mr-12"></i>
                      <div>
                        <div className="text-14 fw-500">{video.title}</div>
                        <div className="text-12 text-light-1">{Math.floor((video.durationSec || 0) / 60)} minutes</div>
                      </div>
                    </div>
                    <div className="d-flex items-center x-gap-8">
                      <button 
                        onClick={() => {
                          console.log('Edit video clicked:', { video, videoId: video._id });
                          if (!video._id) {
                            console.error('Video object missing _id:', video);
                            alert('Error: Video ID is missing. Cannot edit this video.');
                            return;
                          }
                          onEditContent('video', video);
                        }}
                        className="button -sm bg-blue-3 text-blue-1 fw-500"
                        title="Edit video"
                      >
                        <i className="icon-edit"></i>
                      </button>
                      <button 
                        onClick={() => onDeleteContent(video._id, 'video')}
                        className="button -sm bg-red-3 text-red-1 fw-500"
                        title="Delete video"
                      >
                        <i className="icon-bin"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Materials Section */}
        {content.pdfs?.length > 0 && (
          <div className="content-group mt-25">
            <div className="d-flex items-center justify-between mb-15">
              <div className="d-flex items-center">
                <i className="icon-file text-orange-1 mr-8"></i>
                <h5 className="text-16 fw-600">Materials ({content.pdfs.length})</h5>
              </div>
              <button 
                onClick={() => onAddContent('pdf')}
                className="button -sm bg-orange-1 text-white fw-500"
              >
                <i className="icon-plus mr-5"></i>
                Add Material
              </button>
            </div>
            <div className="row y-gap-10">
              {content.pdfs.map((pdf) => (
                <div key={pdf._id} className="col-12">
                  <div className="content-item bg-white rounded-6 px-15 py-12 d-flex items-center justify-between">
                    <div className="d-flex items-center">
                      <i className="icon-file text-orange-1 mr-12"></i>
                      <div>
                        <div className="text-14 fw-500">{pdf.title}</div>
                        <div className="text-12 text-light-1">PDF</div>
                      </div>
                    </div>
                    <div className="d-flex items-center x-gap-8">
                      <button 
                        onClick={() => onEditContent('pdf', pdf)}
                        className="button -sm bg-blue-3 text-blue-1 fw-500"
                        title="Edit PDF"
                      >
                        <i className="icon-edit"></i>
                      </button>
                      <button 
                        onClick={() => onDeleteContent(pdf._id, 'pdf')}
                        className="button -sm bg-red-3 text-red-1 fw-500"
                        title="Delete PDF"
                      >
                        <i className="icon-bin"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mock Tests Section - PDFs with isMockTest: true */}
        {content.mockTests?.length > 0 && (
          <div className="content-group mt-25">
            <div className="d-flex items-center justify-between mb-15">
              <div className="d-flex items-center">
                <i className="icon-test text-red-1 mr-8"></i>
                <h5 className="text-16 fw-600">Mock Tests ({content.mockTests.length})</h5>
              </div>
              <button 
                onClick={() => onAddContent('test')}
                className="button -sm bg-red-1 text-white fw-500"
              >
                <i className="icon-plus mr-5"></i>
                Add Mock Test
              </button>
            </div>
            <div className="row y-gap-10">
              {content.mockTests.map((mockTestPdf) => (
                <div key={mockTestPdf._id} className="col-12">
                  <div className="content-item bg-white rounded-6 px-15 py-12 d-flex items-center justify-between">
                    <div className="d-flex items-center">
                      <i className="icon-file text-red-1 mr-12"></i>
                      <div>
                        <div className="text-14 fw-500">{mockTestPdf.title}</div>
                        <div className="text-12 text-light-1">
                          {mockTestPdf.totalMarks ? `${mockTestPdf.totalMarks} marks` : ''}
                          {mockTestPdf.totalMarks && mockTestPdf.timeLimitMin ? ', ' : ''}
                          {mockTestPdf.timeLimitMin ? `${mockTestPdf.timeLimitMin} minutes` : ''}
                          {!mockTestPdf.totalMarks && !mockTestPdf.timeLimitMin ? 'PDF Mock Test' : ''}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex items-center x-gap-8">
                      <button 
                        onClick={() => onEditContent('pdf', mockTestPdf)}
                        className="button -sm bg-blue-3 text-blue-1 fw-500"
                        title="Edit mock test PDF"
                      >
                        <i className="icon-edit"></i>
                      </button>
                      <button 
                        onClick={() => onDeleteContent(mockTestPdf._id, 'pdf')}
                        className="button -sm bg-red-3 text-red-1 fw-500"
                        title="Delete mock test PDF"
                      >
                        <i className="icon-bin"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentList; 